<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import { createCoupon, listCouponUsages, listCoupons, setCouponStatus, updateCoupon, type CouponStatus, type CouponTemplate, type CouponType, type CouponUsage } from "../api/coupons";

const templates = ref<CouponTemplate[]>([]);
const usages = ref<CouponUsage[]>([]);
const statusFilter = ref<"ALL" | CouponStatus>("ALL");
const editorOpen = ref(false);
const editing = ref<CouponTemplate | null>(null);
const busy = ref(false);
const message = ref("");
const form = reactive({ name: "", type: "STOCK_THRESHOLD" as CouponType, thresholdYuan: "100", discountYuan: "10", totalQuantity: "1000", startsAt: "", expiresAt: "", sortOrder: "0" });

const visibleTemplates = computed(() => statusFilter.value === "ALL" ? templates.value : templates.value.filter((item) => item.status === statusFilter.value));
const yuanToCents = (value: string): number => { const amount = Number(value); return Number.isSafeInteger(Math.round(amount * 100)) && amount > 0 ? Math.round(amount * 100) : 0; };
const toDateInput = (value: string | null): string => value ? new Date(value).toISOString().slice(0, 16) : "";
const typeText = (type: CouponType): string => ({ NEW_USER: "新人券", REFERRAL_WELCOME: "推荐新人券", STOCK_THRESHOLD: "现货满减券", LARGE_PURCHASE: "大额采购券" })[type];
const expiryText = (template: CouponTemplate): string => template.validDays ? `发放后 ${template.validDays} 天` : `${template.startsAt.slice(0, 10)} 至 ${template.expiresAt?.slice(0, 10) ?? "—"}`;
const statusText = (status: CouponStatus): string => status === "ACTIVE" ? "已启用" : "已停用";
const money = (cents: number): string => `¥${(cents / 100).toFixed(2)}`;

async function load(): Promise<void> { templates.value = await listCoupons(); usages.value = await listCouponUsages(); }
function resetForm(): void { Object.assign(form, { name: "", type: "STOCK_THRESHOLD", thresholdYuan: "100", discountYuan: "10", totalQuantity: "1000", startsAt: "", expiresAt: "", sortOrder: "0" }); }
function openCreate(): void { editing.value = null; resetForm(); editorOpen.value = true; message.value = ""; }
function openEdit(template: CouponTemplate): void { editing.value = template; Object.assign(form, { name: template.name, type: template.type, thresholdYuan: (template.thresholdAmount / 100).toFixed(2), discountYuan: (template.discountAmount / 100).toFixed(2), totalQuantity: String(template.totalQuantity), startsAt: toDateInput(template.startsAt), expiresAt: toDateInput(template.expiresAt), sortOrder: String(template.sortOrder) }); editorOpen.value = true; message.value = ""; }
function closeEditor(): void { editorOpen.value = false; editing.value = null; }
async function save(): Promise<void> { const wasEditing = Boolean(editing.value); busy.value = true; message.value = ""; try { const input = { name: form.name.trim(), thresholdAmount: yuanToCents(form.thresholdYuan), discountAmount: yuanToCents(form.discountYuan), totalQuantity: Number(form.totalQuantity), startsAt: new Date(form.startsAt).toISOString(), expiresAt: new Date(form.expiresAt).toISOString(), sortOrder: Number(form.sortOrder) }; if (editing.value) await updateCoupon(editing.value.id, input); else await createCoupon({ ...input, type: form.type, status: "INACTIVE" }); closeEditor(); await load(); message.value = wasEditing ? "优惠券已更新" : "优惠券已创建"; } catch (error) { message.value = error instanceof Error ? error.message : "保存失败"; } finally { busy.value = false; } }
async function toggle(template: CouponTemplate): Promise<void> { busy.value = true; message.value = ""; try { await setCouponStatus(template.id, template.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"); await load(); } catch (error) { message.value = error instanceof Error ? error.message : "状态更新失败"; } finally { busy.value = false; } }
onMounted(() => { void load().catch((error) => { message.value = error instanceof Error ? error.message : "加载失败"; }); });
</script>

<template>
  <section class="coupon-page">
    <header class="page-header coupon-header">
      <div>
        <p class="eyebrow">
          COUPON CENTER
        </p><h1>优惠券管理</h1><p class="muted">
          配置 STOCK 固定金额满减券，优惠金额仅抵扣商品金额。
        </p>
      </div>
      <button
        class="primary-action"
        type="button"
        @click="openCreate"
      >
        + 新增优惠券
      </button>
    </header>
    <p
      v-if="message"
      class="notice panel coupon-message"
      role="status"
    >
      {{ message }}
    </p>
    <section class="panel coupon-panel">
      <div class="coupon-toolbar">
        <div>
          <h2>优惠券模板</h2><p class="muted">
            共 {{ visibleTemplates.length }} 个模板
          </p>
        </div><label>状态<select v-model="statusFilter"><option value="ALL">全部</option><option value="ACTIVE">已启用</option><option value="INACTIVE">已停用</option></select></label>
      </div>
      <div class="coupon-table-wrap">
        <table class="coupon-table">
          <thead><tr><th>名称</th><th>类型</th><th>使用门槛</th><th>优惠金额</th><th>发行量</th><th>有效期</th><th>状态</th><th>操作</th></tr></thead><tbody>
            <tr
              v-for="template in visibleTemplates"
              :key="template.id"
            >
              <td><strong>{{ template.name }}</strong><small>限 STOCK · 每人限领 {{ template.perUserLimit }} 张</small></td><td>{{ typeText(template.type) }}</td><td>{{ money(template.thresholdAmount) }}</td><td class="coupon-money">
                {{ money(template.discountAmount) }}
              </td><td>{{ template.issuedQuantity }} / {{ template.totalQuantity }}</td><td>{{ expiryText(template) }}</td><td>
                <span
                  class="status-badge"
                  :class="template.status === 'ACTIVE' ? 'status-badge--active' : 'status-badge--inactive'"
                >{{ statusText(template.status) }}</span>
              </td><td>
                <div class="row-actions">
                  <button
                    v-if="template.type !== 'REFERRAL_WELCOME'"
                    class="text-action"
                    type="button"
                    @click="openEdit(template)"
                  >
                    编辑
                  </button><button
                    class="text-action text-action--muted"
                    type="button"
                    :disabled="busy"
                    @click="toggle(template)"
                  >
                    {{ template.status === 'ACTIVE' ? '停用' : '启用' }}
                  </button>
                </div>
              </td>
            </tr><tr v-if="visibleTemplates.length === 0">
              <td
                colspan="8"
                class="empty-state"
              >
                暂无优惠券模板
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <section class="panel coupon-usage-panel">
      <div class="coupon-toolbar">
        <div>
          <h2>使用记录</h2><p class="muted">
            仅展示已创建订单中的优惠券快照。
          </p>
        </div>
      </div><div class="coupon-table-wrap">
        <table class="coupon-table coupon-table--usage">
          <thead><tr><th>优惠券</th><th>订单</th><th>用户券</th><th>优惠金额</th><th>使用时间</th></tr></thead><tbody>
            <tr
              v-for="usage in usages"
              :key="usage.id"
            >
              <td>{{ usage.name }}</td><td>{{ usage.orderId }}</td><td>{{ usage.userCouponId }}</td><td class="coupon-money">
                {{ money(usage.discountAmount) }}
              </td><td>{{ usage.createdAt }}</td>
            </tr><tr v-if="usages.length === 0">
              <td
                colspan="5"
                class="empty-state"
              >
                暂无使用记录
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <aside
      v-if="editorOpen"
      class="coupon-drawer"
      aria-label="优惠券编辑"
    >
      <div class="coupon-drawer__head">
        <div>
          <p class="eyebrow">
            {{ editing ? 'EDIT COUPON' : 'NEW COUPON' }}
          </p><h2>{{ editing ? '编辑优惠券' : '新增优惠券' }}</h2>
        </div><button
          class="drawer-close"
          type="button"
          @click="closeEditor"
        >
          ×
        </button>
      </div><form
        class="coupon-form"
        @submit.prevent="save"
      >
        <label>名称<input
          v-model="form.name"
          required
          maxlength="100"
          placeholder="例如：现货采购满减券"
        ></label><label v-if="!editing">类型<select v-model="form.type"><option value="STOCK_THRESHOLD">现货满减券</option><option value="LARGE_PURCHASE">大额采购券</option><option value="NEW_USER">新人券</option></select></label><label>使用门槛（元）<input
          v-model="form.thresholdYuan"
          type="number"
          min="0.01"
          step="0.01"
          required
        ></label><label>优惠金额（元）<input
          v-model="form.discountYuan"
          type="number"
          min="0.01"
          step="0.01"
          required
        ></label><label>总发行量<input
          v-model="form.totalQuantity"
          type="number"
          min="1"
          step="1"
          required
        ></label><label>排序<input
          v-model="form.sortOrder"
          type="number"
          step="1"
          required
        ></label><label>开始时间<input
          v-model="form.startsAt"
          type="datetime-local"
          required
        ></label><label>结束时间<input
          v-model="form.expiresAt"
          type="datetime-local"
          required
        ></label><p class="muted coupon-form__hint">
          金额以元填写，服务端按分保存并在下单时重新校验。适用范围固定为 STOCK。
        </p><div class="coupon-form__actions">
          <button
            class="secondary-action"
            type="button"
            :disabled="busy"
            @click="closeEditor"
          >
            取消
          </button><button
            class="primary-action"
            type="submit"
            :disabled="busy"
          >
            {{ busy ? '保存中…' : '保存' }}
          </button>
        </div>
      </form>
    </aside>
  </section>
</template>

<style scoped>
.coupon-page{display:grid;gap:24px;width:min(100% - 48px,1440px);margin:0 auto}.coupon-header{align-items:start}.coupon-header h1{margin-bottom:8px;letter-spacing:-.035em}.coupon-header .primary-action{margin-top:12px}.coupon-message{margin:0;padding:12px 16px;font-size:13px}.coupon-panel,.coupon-usage-panel{min-width:0;padding:0;overflow:hidden}.coupon-toolbar{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:20px 22px;border-bottom:1px solid var(--admin-outline-soft)}.coupon-toolbar h2{margin:0 0 4px;font-size:17px}.coupon-toolbar p{margin:0;font-size:12px}.coupon-toolbar label{display:flex;align-items:center;gap:8px;color:var(--admin-muted);font-size:12px}.coupon-toolbar select{padding:8px 30px 8px 10px;border:1px solid var(--admin-outline);border-radius:8px;background:#fff;color:var(--admin-ink)}.coupon-table-wrap{overflow-x:auto}.coupon-table{width:100%;min-width:900px;border-collapse:collapse;text-align:left}.coupon-table th,.coupon-table td{padding:14px 22px;border-bottom:1px solid var(--admin-outline-soft);vertical-align:middle}.coupon-table th{background:#f8faf9;color:var(--admin-muted);font-size:12px;font-weight:700;white-space:nowrap}.coupon-table td{color:var(--admin-ink);font-size:13px}.coupon-table td:first-child{display:grid;gap:3px}.coupon-table td small{color:var(--admin-muted);font-size:11px}.coupon-money{color:var(--admin-primary-deep)!important;font-variant-numeric:tabular-nums;font-weight:700;white-space:nowrap}.coupon-drawer{position:fixed;z-index:30;top:0;right:0;display:grid;width:min(480px,100vw);height:100vh;align-content:start;overflow:auto;background:#fff;box-shadow:-12px 0 30px rgba(28,67,46,.12)}.coupon-drawer__head{display:flex;align-items:start;justify-content:space-between;padding:28px 28px 12px;border-bottom:1px solid var(--admin-outline-soft)}.coupon-drawer__head h2{margin:6px 0 0}.drawer-close{border:0;background:transparent;color:var(--admin-muted);font-size:28px}.coupon-form{display:grid;gap:16px;padding:24px 28px}.coupon-form label{display:grid;gap:7px;color:var(--admin-muted);font-size:13px;font-weight:700}.coupon-form input,.coupon-form select{width:100%;padding:10px 12px;border:1px solid var(--admin-outline);border-radius:8px;background:#fff;color:var(--admin-ink);font-size:13px;font-weight:400}.coupon-form__hint{margin:0;font-size:12px;line-height:1.6}.coupon-form__actions{display:flex;justify-content:flex-end;gap:10px;padding-top:12px;border-top:1px solid var(--admin-outline-soft)}.coupon-form__actions .primary-action{min-width:100px}@media(max-width:720px){.coupon-page{width:calc(100% - 32px)}.coupon-header{align-items:stretch;flex-direction:column}.coupon-header .primary-action{align-self:flex-start;margin-top:0}.coupon-toolbar{align-items:stretch;flex-direction:column}.coupon-drawer__head,.coupon-form{padding-right:20px;padding-left:20px}}
</style>

